import type { AssetType, Stored } from "@overmap-ai/common"
import type { ReactNode } from "react"

import {
	AssetTypeEqualsFilterModifier,
	AssetTypeExcludesFilterModifier,
	AssetTypeIncludesFilterModifier,
	AssetTypeNotEqualsFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { AssetTypeFilterCell } from "./AssetTypeFilterCell"

type AssetTypeFieldValue = AssetType["uuid"]
type AssetTypeFilterValue = AssetType["uuid"][] | AssetType["uuid"]

export class AssetTypeFilterDef extends BaseFilterDef<AssetTypeFieldValue, AssetTypeFilterValue> {
	readonly id = "asset_type"
	readonly defaultFilterValue: AssetTypeFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"

	readonly assetTypes: Stored<AssetType>[]

	constructor(assetTypes: Stored<AssetType>[]) {
		super()
		this.assetTypes = assetTypes
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: AssetTypeEqualsFilterModifier,
			isValueValid: (_value: AssetTypeFieldValue): _value is AssetType["uuid"] => true,
			isFilterValueValid: (filterValue: AssetTypeFilterValue): filterValue is AssetType["uuid"] =>
				!Array.isArray(filterValue),
		}),
		notEquals: createFilterModifierConfig({
			modifier: AssetTypeNotEqualsFilterModifier,
			isValueValid: (_value: AssetTypeFieldValue): _value is AssetType["uuid"] => true,
			isFilterValueValid: (filterValue: AssetTypeFilterValue): filterValue is AssetType["uuid"] =>
				!Array.isArray(filterValue),
		}),
		includes: createFilterModifierConfig({
			modifier: AssetTypeIncludesFilterModifier,
			isValueValid: (_value: AssetTypeFieldValue): _value is AssetType["uuid"] => true,
			isFilterValueValid: (filterValue: AssetTypeFilterValue): filterValue is AssetType["uuid"][] =>
				Array.isArray(filterValue),
		}),
		excludes: createFilterModifierConfig({
			modifier: AssetTypeExcludesFilterModifier,
			isValueValid: (_value: AssetTypeFieldValue): _value is AssetType["uuid"] => true,
			isFilterValueValid: (filterValue: AssetTypeFilterValue): filterValue is AssetType["uuid"][] =>
				Array.isArray(filterValue),
		}),
	}

	render(props: FilterDefRenderProps<AssetTypeFilterDef>): ReactNode {
		return <AssetTypeFilterCell key={props.filter.id} {...props} />
	}
}
